import { LogOut } from 'lucide-react';
import { cn, isSymbolicaUser } from '@/lib/utils';
import { useAuth } from '@/hooks/useAuth';
import { useDocumentTitle } from '@/hooks/useDocumentTitle';
import { useSettings } from '@/hooks/useSettings';
import { usePaperThemeActive } from '@/hooks/useThemeFlag';
import { Titles } from '@/lib/navigation';
import { ContentCard } from '../layout/ContentCard';
import { IconFrame } from '../layout/IconFrame';
import { SettingsShell } from './SettingsShell';
import { GeneralSettingsPaper } from '../paper/GeneralSettingsPaper';
import {
  trackActivityVisibilityChanged,
  trackLogout,
} from '@/analytics';

export const GeneralSettingsView = () => {
  useDocumentTitle(Titles.settings());
  const isPaper = usePaperThemeActive();
  const { isAuthenticated, user, logout } = useAuth();
  const { settings, setActivityVisibility } = useSettings();
  const canViewAllActivity = isSymbolicaUser(user?.email);
  const activityVisibility = canViewAllActivity ? settings.activityVisibility : 'results_only';

  if (isPaper) return <GeneralSettingsPaper />;

  return (
    <SettingsShell activeTab="general">
      <div className="mt-6 space-y-6">
        {isAuthenticated && user && (
          <ContentCard className="bg-white/[0.02] p-5">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <IconFrame className="h-11 w-11 rounded-full bg-white/[0.08] text-[15px] font-medium text-zinc-300">
                  {user.firstName.charAt(0)}
                  {user.lastName.charAt(0)}
                </IconFrame>
                <div>
                  <p className="text-[14px] font-medium text-zinc-200">
                    {user.firstName} {user.lastName}
                  </p>
                  <p className="text-[12px] text-zinc-500">{user.email}</p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => { trackLogout(); logout(); }}
                className="flex h-9 items-center gap-2 rounded-lg border border-white/[0.08] px-3 text-[13px] text-zinc-400 transition-colors hover:bg-white/[0.03] hover:text-zinc-300"
              >
                <LogOut className="h-3.5 w-3.5" />
                Log out
              </button>
            </div>
          </ContentCard>
        )}

        {canViewAllActivity && (
          <div className="space-y-4">
            <div>
              <label className="mb-2 block text-[12px] text-zinc-500">Activity Display</label>
              <p className="mb-3 text-[12px] text-zinc-600">
                Control what is shown in the conversation timeline.
              </p>
              <div className="flex overflow-hidden rounded-lg border border-white/[0.06]">
                <button
                  type="button"
                  onClick={() => { setActivityVisibility('show_all'); trackActivityVisibilityChanged('show_all'); }}
                  className={cn(
                    'flex-1 px-4 py-2.5 text-[13px] transition-colors',
                    activityVisibility === 'show_all'
                      ? 'bg-white/[0.08] text-zinc-100'
                      : 'text-zinc-500 hover:bg-white/[0.03] hover:text-zinc-300',
                  )}
                >
                  All
                </button>
                <button
                  type="button"
                  onClick={() => { setActivityVisibility('results_only'); trackActivityVisibilityChanged('results_only'); }}
                  className={cn(
                    'flex-1 border-l border-white/[0.06] px-4 py-2.5 text-[13px] transition-colors',
                    activityVisibility === 'results_only'
                      ? 'bg-white/[0.08] text-zinc-100'
                      : 'text-zinc-500 hover:bg-white/[0.03] hover:text-zinc-300',
                  )}
                >
                  Results only
                </button>
              </div>
            </div>

          </div>
        )}
      </div>
    </SettingsShell>
  );
};
